import { Search, SlidersHorizontal, LayoutGrid, List } from 'lucide-react'
import { Button } from '@/components/ui/button'

interface ListingHeaderProps {
  title: string
  count: number
  searchQuery: string
  onSearchChange: (query: string) => void
  layout: 'grid' | 'list'
  onLayoutChange: (layout: 'grid' | 'list') => void
  sortBy: string
  onSortChange: (sort: string) => void
  onToggleMobileFilter?: () => void
}

export function ListingHeader({
  title,
  count,
  searchQuery,
  onSearchChange,
  layout,
  onLayoutChange,
  sortBy,
  onSortChange,
  onToggleMobileFilter,
}: ListingHeaderProps) {
  return (
    <div className="space-y-4">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="font-display text-2xl sm:text-3xl font-extrabold text-foreground">{title}</h1>
          <p className="text-xs text-muted-foreground">Showing {count} verified listings</p>
        </div>

        {/* Action Controls */}
        <div className="flex items-center gap-3">
          {/* Mobile Filter Toggle */}
          {onToggleMobileFilter && (
            <Button
              size="sm"
              variant="outline"
              onClick={onToggleMobileFilter}
              className="lg:hidden border-border text-foreground hover:bg-accent"
            >
              <SlidersHorizontal className="h-4 w-4 mr-1.5" /> Filters
            </Button>
          )}

          {/* Sort Dropdown */}
          <select
            value={sortBy}
            onChange={(e) => onSortChange(e.target.value)}
            className="h-9 rounded-xl bg-card border border-border px-3 text-xs text-foreground focus:border-primary focus:outline-none"
          >
            <option value="featured" className="bg-popover text-popover-foreground">Sort by: Featured</option>
            <option value="price-low" className="bg-popover text-popover-foreground">Price: Low to High</option>
            <option value="price-high" className="bg-popover text-popover-foreground">Price: High to Low</option>
            <option value="newest" className="bg-popover text-popover-foreground">Newest Additions</option>
          </select>

          {/* Grid / List View Toggle */}
          <div className="hidden sm:flex items-center rounded-xl bg-card border border-border p-1">
            <button
              onClick={() => onLayoutChange('grid')}
              className={`p-1.5 rounded-lg transition-colors ${layout === 'grid' ? 'bg-primary text-white' : 'text-muted-foreground hover:text-foreground'}`}
              aria-label="Grid view"
            >
              <LayoutGrid className="h-4 w-4" />
            </button>
            <button
              onClick={() => onLayoutChange('list')}
              className={`p-1.5 rounded-lg transition-colors ${layout === 'list' ? 'bg-primary text-white' : 'text-muted-foreground hover:text-foreground'}`}
              aria-label="List view"
            >
              <List className="h-4 w-4" />
            </button>
          </div>
        </div>
      </div>

      {/* Search Input Bar */}
      <div className="relative w-full">
        <input
          type="text"
          placeholder="Search by title, brand, or model..."
          value={searchQuery}
          onChange={(e) => onSearchChange(e.target.value)}
          className="w-full h-11 rounded-xl bg-card border border-border pl-11 pr-4 text-xs text-foreground placeholder:text-muted-foreground focus:border-primary focus:outline-none shadow-xs"
        />
        <Search className="absolute left-4 top-3.5 h-4 w-4 text-muted-foreground" />
      </div>
    </div>
  )
}
